import React, { memo, useState, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { Dropdown } from "@UI/Dropdown/Dropdown";
import { useOnOutsideClick } from "@hooks/useOnOutsideClick";
import UserProfileIcon from "@assets/svgs/UserProfileSvg";
import ArrowDownSvg from "@assets/svgs/ArrowDownSvg";
import { useTranslation } from "react-i18next";

import styles from "./UserMenu.module.css";

/**
 * UserMenu – user button with dropdown (profile, language, logout, etc.).
 * Header-style only.
 */
const UserMenu = memo(
  ({
    userImageUrl,
    username,
    isOpen,
    onToggle,
    dropdownData,
    onItemClick,
    collapsed,
  }) => {
    const { t } = useTranslation();
    const menuRef = useRef(null);
    const triggerRef = useRef(null);
    const [hasImageError, setHasImageError] = useState(false);

    const handleClickOutside = useCallback(() => {
      if (isOpen) onToggle(false);
    }, [isOpen, onToggle]);

    useOnOutsideClick(menuRef, handleClickOutside);

    const handleKeyDown = useCallback(
      (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onToggle(!isOpen);
        } else if (e.key === "Escape" && isOpen) {
          e.preventDefault();
          onToggle(false);
        }
      },
      [isOpen, onToggle],
    );

    const handleImageError = useCallback(() => setHasImageError(true), []);
    const handleDropdownItemClick = useCallback(
      (item) => {
        if (!item) {
          onToggle(false);
          triggerRef.current?.focus();
          return;
        }
        onItemClick(item);
      },
      [onToggle, onItemClick],
    );

    const isImageUrl =
      typeof userImageUrl === "string" && userImageUrl.startsWith("http");
    const shouldShowImage = isImageUrl && !hasImageError;
    const menuLabel = t("topBar.userMenu", {
      defaultValue: "Account menu for {{username}}",
      username,
    });

    const wrapperClass = styles.userMenuWrapper;

    return (
      <div className={wrapperClass} ref={menuRef}>
        <button
          ref={triggerRef}
          onClick={() => onToggle(!isOpen)}
          className={`${styles.userInput} ${isOpen ? styles.active : ""} ${collapsed ? styles.collapsed : ""}`}
          type="button"
          aria-label={menuLabel}
          title={username}
          aria-expanded={isOpen}
          aria-haspopup="true"
          onKeyDown={handleKeyDown}
        >
          {shouldShowImage ? (
            <img
              src={userImageUrl}
              alt=""
              onError={handleImageError}
              loading="lazy"
            />
          ) : typeof userImageUrl === "string" ? (
            <UserProfileIcon />
          ) : (
            userImageUrl
          )}
          <div className={styles.usernameWrapper}>
            <span className={styles.username} title={username}>
              {username}
            </span>
          </div>
          <ArrowDownSvg
            active={isOpen}
            aria-hidden="true"
            className={styles.arrowIcon}
            activeClassName={styles.arrowIconRotate}
          />
        </button>

        {isOpen && (
          <Dropdown
            data={dropdownData}
            type="dropdown"
            dropdown="dropdown"
            active={isOpen}
            className={styles.dropdown}
            ariaLabel={t("topBar.accountOptions", "Account menu options")}
            customStyles={{
              backgroundColor: "var(--popup-background)",
            }}
            handleListItemClick={handleDropdownItemClick}
          />
        )}
      </div>
    );
  },
);

UserMenu.displayName = "UserMenu";

UserMenu.propTypes = {
  userImageUrl: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
    .isRequired,
  username: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  dropdownData: PropTypes.array.isRequired,
  onItemClick: PropTypes.func.isRequired,
  collapsed: PropTypes.bool,
};

export default UserMenu;
